export interface BoundingBox {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Geographic bounding boxes for all 50 US states and 13 Canadian provinces/territories.
 * Used to determine which GeoJSON files to load based on the map viewport.
 */
export const STATE_BOUNDS: Record<string, BoundingBox> = {
  // ─── US States ──────────────────────────────────────────────────────────────
  AL: { north: 35.008, south: 30.221, east: -84.889, west: -88.473 },
  AK: { north: 71.538, south: 51.214, east: -129.979, west: -179.148 },
  AZ: { north: 37.004, south: 31.332, east: -109.045, west: -114.816 },
  AR: { north: 36.5, south: 33.004, east: -89.644, west: -94.617 },
  CA: { north: 42.009, south: 32.534, east: -114.131, west: -124.409 },
  CO: { north: 41.003, south: 36.993, east: -102.041, west: -109.06 },
  CT: { north: 42.05, south: 40.987, east: -71.787, west: -73.728 },
  DE: { north: 39.839, south: 38.451, east: -75.049, west: -75.789 },
  FL: { north: 31.001, south: 24.396, east: -79.974, west: -87.635 },
  GA: { north: 35.001, south: 30.357, east: -80.84, west: -85.605 },
  HI: { north: 22.235, south: 18.911, east: -154.806, west: -160.247 },
  ID: { north: 49.001, south: 41.988, east: -111.044, west: -117.243 },
  IL: { north: 42.508, south: 36.97, east: -87.495, west: -91.513 },
  IN: { north: 41.761, south: 37.772, east: -84.784, west: -88.098 },
  IA: { north: 43.501, south: 40.375, east: -90.14, west: -96.639 },
  KS: { north: 40.003, south: 36.993, east: -94.588, west: -102.052 },
  KY: { north: 39.147, south: 36.497, east: -81.965, west: -89.571 },
  LA: { north: 33.019, south: 28.928, east: -88.817, west: -94.043 },
  ME: { north: 47.46, south: 43.065, east: -66.95, west: -71.084 },
  MD: { north: 39.723, south: 37.912, east: -75.049, west: -79.487 },
  MA: { north: 42.887, south: 41.237, east: -69.929, west: -73.508 },
  MI: { north: 48.306, south: 41.696, east: -82.413, west: -90.418 },
  MN: { north: 49.384, south: 43.5, east: -89.489, west: -97.239 },
  MS: { north: 35.008, south: 30.174, east: -88.098, west: -91.655 },
  MO: { north: 40.613, south: 35.996, east: -89.099, west: -95.774 },
  MT: { north: 49.001, south: 44.358, east: -104.039, west: -116.05 },
  NE: { north: 43.002, south: 39.999, east: -95.308, west: -104.054 },
  NV: { north: 42.002, south: 35.002, east: -114.04, west: -120.006 },
  NH: { north: 45.306, south: 42.697, east: -70.703, west: -72.557 },
  NJ: { north: 41.357, south: 38.928, east: -73.894, west: -75.563 },
  NM: { north: 37.0, south: 31.332, east: -103.002, west: -109.05 },
  NY: { north: 45.016, south: 40.477, east: -71.856, west: -79.762 },
  NC: { north: 36.588, south: 33.842, east: -75.46, west: -84.322 },
  ND: { north: 49.001, south: 45.935, east: -96.554, west: -104.049 },
  OH: { north: 41.978, south: 38.403, east: -80.519, west: -84.82 },
  OK: { north: 37.002, south: 33.616, east: -94.431, west: -103.003 },
  OR: { north: 46.292, south: 41.992, east: -116.463, west: -124.567 },
  PA: { north: 42.269, south: 39.72, east: -74.69, west: -80.519 },
  RI: { north: 42.019, south: 41.146, east: -71.12, west: -71.907 },
  SC: { north: 35.215, south: 32.035, east: -78.542, west: -83.354 },
  SD: { north: 45.945, south: 42.48, east: -96.436, west: -104.058 },
  TN: { north: 36.678, south: 34.983, east: -81.647, west: -90.31 },
  TX: { north: 36.501, south: 25.837, east: -93.508, west: -106.646 },
  UT: { north: 42.001, south: 36.998, east: -109.041, west: -114.053 },
  VT: { north: 45.017, south: 42.727, east: -71.465, west: -73.438 },
  VA: { north: 39.466, south: 36.541, east: -75.242, west: -83.675 },
  WA: { north: 49.002, south: 45.544, east: -116.916, west: -124.849 },
  WV: { north: 40.638, south: 37.202, east: -77.719, west: -82.644 },
  WI: { north: 47.08, south: 42.492, east: -86.806, west: -92.889 },
  WY: { north: 45.006, south: 40.995, east: -104.052, west: -111.057 },
  DC: { north: 38.996, south: 38.792, east: -76.909, west: -77.12 },

  // ─── Canadian Provinces & Territories ───────────────────────────────────────
  ON: { north: 56.852, south: 41.681, east: -74.343, west: -95.154 },
  QC: { north: 62.584, south: 44.996, east: -57.1, west: -79.764 },
  BC: { north: 60.0, south: 48.225, east: -114.054, west: -139.057 },
  AB: { north: 60.0, south: 49.0, east: -110.005, west: -120.001 },
  SK: { north: 60.0, south: 49.0, east: -101.361, west: -110.005 },
  MB: { north: 60.0, south: 49.0, east: -88.941, west: -102.001 },
  NB: { north: 48.07, south: 44.601, east: -63.776, west: -69.065 },
  NS: { north: 47.031, south: 43.422, east: -59.684, west: -66.418 },
  PE: { north: 47.073, south: 45.946, east: -61.97, west: -64.424 },
  NL: { north: 60.374, south: 46.616, east: -52.616, west: -67.812 },
  YT: { north: 69.647, south: 60.0, east: -124.0, west: -141.002 },
  NT: { north: 78.77, south: 60.0, east: -102.001, west: -136.447 },
  NU: { north: 83.113, south: 51.654, east: -61.241, west: -120.376 },
};

/** US state codes for distinguishing US vs Canadian regions */
const US_STATES = new Set([
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
]);

export function isUsState(code: string): boolean {
  return US_STATES.has(code.toUpperCase());
}

/**
 * Returns region codes whose bounding boxes overlap with the given bounds.
 */
export function getRegionsInBounds(bounds: {
  north: number;
  south: number;
  east: number;
  west: number;
}): string[] {
  return Object.entries(STATE_BOUNDS)
    .filter(
      ([, box]) =>
        box.south <= bounds.north &&
        box.north >= bounds.south &&
        box.west <= bounds.east &&
        box.east >= bounds.west,
    )
    .map(([code]) => code);
}
